/**
 * DRUG-DATA 장시간 작업 플래그 폴링 — 레거시 `useDrugDataFlagTaskPolling.js` 포팅.
 *
 * 레거시: 요청 전 updatedAt을 찍고, HTTP가 끝나거나 타임아웃이 나도 flags로 완료를 판정.
 * 배치처럼 접수만 하는 API는 응답을 기다리지 않고 pending 직후 폴링을 시작한다.
 * 동기 API(DUR 업로드 등)는 응답 시점에 이미 flag가 갱신되므로, 응답 직후 즉시 settle 한다.
 *
 * 진행 중 작업은 localStorage 에 년월·작업 단위로 남겨, 새로고침해도 처리 중 표시와 폴링을 이어간다.
 * 서버 flags 에는 "진행 중" 값이 없어 실패하면 완료가 오지 않으므로, 오래된 작업은 만료시킨다.
 */
import type { DrugDataFlags } from '@/entities/drug-data/api/drug-data.api';
import { isAxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

export const DRUG_DATA_FLAG_POLL_INTERVAL_MS = 5000;

/** 이 시간이 지나도 완료가 확인되지 않으면 대기를 멈춘다(서버 실패 시 영구 '처리 중' 방지) */
export const DRUG_DATA_FLAG_TASK_TTL_MS = 60 * 60 * 1000;

export const DRUG_DATA_FLAG_TASKS_STORAGE_KEY =
  'ruda.drugData.pendingFlagTasks';

export type DrugDataFlagTaskKey =
  | 'batchFlag'
  | 'mergeFlag'
  | 'updateFlag'
  | 'durFlag'
  | 'codeMappingDone';

const TASK_CONFIG: Record<
  DrugDataFlagTaskKey,
  { updatedAtKey: keyof DrugDataFlags; completeLabel: string }
> = {
  batchFlag: {
    updatedAtKey: 'batchFlagUpdatedAt',
    completeLabel: '데이터 수집 배치',
  },
  mergeFlag: {
    updatedAtKey: 'mergeFlagUpdatedAt',
    completeLabel: 'HIRA 병합',
  },
  updateFlag: {
    updatedAtKey: 'updateFlagUpdatedAt',
    completeLabel: '의약품 정보 업데이트',
  },
  durFlag: {
    updatedAtKey: 'durFlagUpdatedAt',
    completeLabel: 'DUR 비급여 처리',
  },
  codeMappingDone: {
    updatedAtKey: 'codeMappingFlagUpdatedAt',
    completeLabel: '코드 매핑',
  },
};

export interface DrugDataFlagPendingTask {
  baseYm: string;
  baselineUpdatedAt?: string;
}

/** 저장·복원 단위. 같은 작업이라도 년월이 다르면 따로 기다린다 */
export interface StoredDrugDataFlagTask extends DrugDataFlagPendingTask {
  flagKey: DrugDataFlagTaskKey;
  /** 요청 시각(ms). 만료 판정 기준 */
  startedAt: number;
}

type PendingFlagTasks = Record<string, StoredDrugDataFlagTask>;

const toTaskId = (baseYm: string, flagKey: DrugDataFlagTaskKey) =>
  `${baseYm}:${flagKey}`;

export const isDrugDataFlagTaskExpired = (
  task: Pick<StoredDrugDataFlagTask, 'startedAt'>,
  now: number = Date.now(),
): boolean => now - task.startedAt > DRUG_DATA_FLAG_TASK_TTL_MS;

const isStoredTask = (value: unknown): value is StoredDrugDataFlagTask => {
  if (!value || typeof value !== 'object') return false;
  const task = value as Partial<StoredDrugDataFlagTask>;
  return (
    typeof task.baseYm === 'string' &&
    typeof task.startedAt === 'number' &&
    typeof task.flagKey === 'string' &&
    task.flagKey in TASK_CONFIG
  );
};

/** 저장된 진행 중 작업을 읽는다. 깨진 값·만료된 작업은 버린다 (저장소 접근 실패 시 빈 값) */
export const loadPendingDrugDataFlagTasks = (
  now: number = Date.now(),
): PendingFlagTasks => {
  try {
    const raw = window.localStorage.getItem(DRUG_DATA_FLAG_TASKS_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return {};
    return Object.fromEntries(
      parsed
        .filter(isStoredTask)
        .filter((task) => !isDrugDataFlagTaskExpired(task, now))
        .map((task) => [toTaskId(task.baseYm, task.flagKey), task]),
    );
  } catch {
    return {};
  }
};

const savePendingDrugDataFlagTasks = (tasks: PendingFlagTasks) => {
  try {
    const list = Object.values(tasks);
    if (list.length === 0) {
      window.localStorage.removeItem(DRUG_DATA_FLAG_TASKS_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(
      DRUG_DATA_FLAG_TASKS_STORAGE_KEY,
      JSON.stringify(list),
    );
  } catch {
    // 시크릿 모드 등 저장 불가 — 새로고침 복원만 안 될 뿐 폴링은 그대로 동작한다
  }
};

const readTaskFlag = (
  flags: DrugDataFlags,
  flagKey: DrugDataFlagTaskKey,
): unknown => {
  if (flagKey === 'codeMappingDone') {
    return flags.codeMappingDone ?? flags.codeMappingFlag;
  }
  return flags[flagKey];
};

/** flag=true 이고, 재실행이면 updatedAt이 요청 시점과 달라져야 완료 */
export const isDrugDataFlagTaskComplete = (
  flags: DrugDataFlags,
  flagKey: DrugDataFlagTaskKey,
  task: DrugDataFlagPendingTask,
): boolean => {
  if (!readTaskFlag(flags, flagKey)) return false;
  const nextUpdatedAt = flags[TASK_CONFIG[flagKey].updatedAtKey];
  if (!nextUpdatedAt) return true;
  return String(nextUpdatedAt) !== String(task.baselineUpdatedAt ?? '');
};

export const isDrugDataRequestTimeout = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  return error.code === 'ECONNABORTED' || /timeout/i.test(error.message ?? '');
};

export const useDrugDataFlagPolling = (
  baseYm: string,
  fetchFlags: () => Promise<DrugDataFlags>,
  onComplete?: (flagKey: DrugDataFlagTaskKey) => void,
  /** 만료 시간 안에 완료를 확인하지 못해 대기를 멈췄을 때 */
  onExpire?: (flagKey: DrugDataFlagTaskKey) => void,
) => {
  const [pending, setPending] = useState<PendingFlagTasks>(() =>
    loadPendingDrugDataFlagTasks(),
  );
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  useEffect(() => {
    savePendingDrugDataFlagTasks(pending);
  }, [pending]);

  const clearTask = useCallback(
    (flagKey: DrugDataFlagTaskKey) => {
      const taskId = toTaskId(baseYm, flagKey);
      setPending((prev) => {
        if (!prev[taskId]) return prev;
        const next = { ...prev };
        delete next[taskId];
        pendingRef.current = next;
        return next;
      });
    },
    [baseYm],
  );

  const completeTask = useCallback(
    (flagKey: DrugDataFlagTaskKey) => {
      clearTask(flagKey);
      onComplete?.(flagKey);
    },
    [clearTask, onComplete],
  );

  const isTaskPending = useCallback(
    (flagKey: DrugDataFlagTaskKey) =>
      Boolean(pendingRef.current[toTaskId(baseYm, flagKey)]),
    [baseYm],
  );

  /**
   * 요청 직전에 동기 호출 — flags GET을 기다리지 않고 바로 처리 중 UI를 켠다.
   * baseline은 화면에 이미 있는 flags를 쓴다.
   */
  const beginTask = useCallback(
    (flagKey: DrugDataFlagTaskKey, currentFlags?: DrugDataFlags | null) => {
      const taskId = toTaskId(baseYm, flagKey);
      if (pendingRef.current[taskId]) return;
      const config = TASK_CONFIG[flagKey];
      const task: StoredDrugDataFlagTask = {
        flagKey,
        baseYm,
        baselineUpdatedAt: String(currentFlags?.[config.updatedAtKey] ?? ''),
        startedAt: Date.now(),
      };
      pendingRef.current = { ...pendingRef.current, [taskId]: task };
      setPending(pendingRef.current);
    },
    [baseYm],
  );

  /** HTTP 성공 직후 호출 — 이미 끝난 동기 작업은 폴링 없이 바로 완료 */
  const settleTask = useCallback(
    async (flagKey: DrugDataFlagTaskKey) => {
      const flags = await fetchFlags();
      const task = pendingRef.current[toTaskId(baseYm, flagKey)];
      if (!task) return;
      if (isDrugDataFlagTaskComplete(flags, flagKey, task)) {
        completeTask(flagKey);
      }
    },
    [baseYm, completeTask, fetchFlags],
  );

  const cancelTask = useCallback(
    (flagKey: DrugDataFlagTaskKey) => {
      clearTask(flagKey);
    },
    [clearTask],
  );

  // 화면은 지금 보고 있는 년월의 작업만 처리 중으로 본다(다른 년월 작업은 그 년월로 돌아오면 이어간다)
  const pendingKeys: string[] = Object.values(pending)
    .filter((task) => task.baseYm === baseYm)
    .map((task) => task.flagKey);
  const pendingSignature = pendingKeys.join(',');

  useEffect(() => {
    if (!pendingSignature) return undefined;

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      const now = Date.now();
      const waiting: DrugDataFlagTaskKey[] = [];
      for (const task of Object.values(pendingRef.current)) {
        if (task.baseYm !== baseYm) continue;
        if (isDrugDataFlagTaskExpired(task, now)) {
          clearTask(task.flagKey);
          onExpire?.(task.flagKey);
        } else {
          waiting.push(task.flagKey);
        }
      }
      if (!waiting.length) return;
      try {
        const flags = await fetchFlags();
        if (cancelled) return;
        for (const flagKey of waiting) {
          const task = pendingRef.current[toTaskId(baseYm, flagKey)];
          if (!task) continue;
          if (isDrugDataFlagTaskComplete(flags, flagKey, task)) {
            completeTask(flagKey);
          }
        }
      } catch {
        // 폴링 실패는 무시하고 다음 주기에 재시도
      }
    };

    void poll();
    const timer = window.setInterval(() => {
      void poll();
    }, DRUG_DATA_FLAG_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [baseYm, clearTask, completeTask, fetchFlags, onExpire, pendingSignature]);

  return {
    beginTask,
    settleTask,
    cancelTask,
    isTaskPending,
    isPolling: pendingKeys.length > 0,
    pendingKeys,
  };
};
